// frontend/src/components/common/GettingStartedChecklist.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronRight, X, Rocket } from 'lucide-react';
import api from '../../utils/api';

const DISMISSED_KEY = 'stb_checklist_dismissed';
const FORECAST_VIEWED_KEY = 'stb_forecast_viewed';

export default function GettingStartedChecklist() {
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === 'true'; } catch { return false; }
  });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    async function fetchChecklist() {
      try {
        const { data } = await api.get('/api/user/onboarding-checklist');
        if (!cancelled) {
          // Override forecast_viewed with localStorage flag
          const forecastViewed = (() => {
            try { return localStorage.getItem(FORECAST_VIEWED_KEY) === 'true'; } catch { return false; }
          })();
          const items = data.items.map(item =>
            item.key === 'forecast_viewed' ? { ...item, completed: forecastViewed || item.completed } : item
          );
          // FIX 2: Calculate totalCount from items.length for defensive handling
          const totalCount = items.length;
          const completedCount = items.filter(i => i.completed).length;
          setChecklist({ items, totalCount, completedCount });
        }
      } catch {
        // Fail silently — checklist is non-critical
      }
    }
    fetchChecklist();
    return () => { cancelled = true; };
  }, [dismissed, refreshKey]);

  // FIX 1: Re-fetch checklist when user returns to Dashboard
  useEffect(() => {
    if (dismissed) return;
    const handleFocus = () => {
      setRefreshKey(prev => prev + 1);
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISSED_KEY, 'true'); } catch { /* ignore */ }
  };

  const handleNavigate = (href) => {
    // Mark forecast as viewed when clicking that checklist item
    if (href === '/planner/forecast') {
      try { localStorage.setItem(FORECAST_VIEWED_KEY, 'true'); } catch { /* ignore */ }
    }
    navigate(href);
  };

  // Don't show if: dismissed, still loading, or all complete
  if (dismissed || !checklist) return null;
  if (checklist.completedCount >= checklist.totalCount) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-emerald-600" />
          <h3 className="font-semibold text-gray-900">Get Started</h3>
          <span className="text-sm text-gray-500">
            {checklist.completedCount} of {checklist.totalCount} complete
          </span>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Dismiss checklist"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-100 rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-emerald-500 rounded-full transition-all duration-500"
          style={{ width: `${(checklist.completedCount / checklist.totalCount) * 100}%` }}
        />
      </div>

      {/* Items */}
      <ul className="space-y-1">
        {checklist.items.map((item) => (
          <li key={item.key}>
            <button
              onClick={() => !item.completed && handleNavigate(item.href)}
              disabled={item.completed}
              className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                item.completed
                  ? 'text-gray-400 cursor-default'
                  : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
              }`}
            >
              {item.completed ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
              )}
              <span className={`flex-1 text-sm ${item.completed ? 'line-through' : 'font-medium'}`}>
                {item.label}
              </span>
              {!item.completed && (
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
